/**
 * Add a person — the Application slideover's kebab action
 *
 * A household is rarely settled when the form is submitted. A partner is named
 * a week later; a parent offers to stand behind the rent once referencing comes
 * back conditional. Both are "add a person", and the only thing that really
 * differs is what we need to know about them — so role is the first question
 * the form asks, and the rest of it follows from the answer.
 *
 * Applicant → POST /tenancy-applications/:id/applicants
 * Guarantor → POST /tenancy-applications/:id/guarantees
 *
 * Requirements on the consuming component:
 *   - `this.applicationId`, `this.application`
 *   - `this.fetchApplication()`, `this.fetchGuarantees(force)` — from the
 *     guarantee tabs mixin, to redraw the tabs once someone new exists
 */

const BLANK_PERSON = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address_line_1: '',
  address_line_2: '',
  town: '',
  county: '',
  postcode: ''
}

export function addPersonMixin() {
  return {
    addPersonOpen: false,
    addPersonSaving: false,
    addPersonError: null,

    addPerson: {
      role: 'applicant',
      // 'new' types somebody in; 'existing' finds a person already on the books
      // — a guarantor standing behind two sharers, or a returning tenant.
      mode: 'new',
      ...BLANK_PERSON,

      // Applicant-only
      date_of_birth: '',
      relationship_to_lead: 'partner',
      employment_status: '',
      annual_income: '',
      job_title: '',
      send_invitation: true,

      // Guarantor-only. `guaranteed` holds a Person id when the applicant has a
      // record, or 'email:<address>' when they are still only on the form.
      guaranteed: '',
      relationship: 'child',
      rent_liability_percentage: 100,
      declared_annual_income: '',
      occupation: ''
    },

    // Person search, for the "already in LetAdmin" case
    addPersonQuery: '',
    addPersonResults: [],
    addPersonSearching: false,
    addPersonSelected: null,
    addPersonSearchTimer: null,

    RELATIONSHIPS_TO_LEAD: [
      { value: 'partner', label: 'Partner or spouse' },
      { value: 'family', label: 'Family member' },
      { value: 'friend', label: 'Friend' },
      { value: 'sharer', label: 'Sharer (unrelated)' },
      { value: 'other', label: 'Other' }
    ],

    EMPLOYMENT_STATUSES: [
      { value: 'employed', label: 'Employed' },
      { value: 'self_employed', label: 'Self-employed' },
      { value: 'retired', label: 'Retired' },
      { value: 'student', label: 'Student' },
      { value: 'unemployed', label: 'Not currently working' }
    ],

    // ========== Opening and closing ==========

    openAddPerson(role = 'applicant') {
      const applicants = this.guaranteeData?.applicants || []

      this.addPersonError = null
      this.addPersonQuery = ''
      this.addPersonResults = []
      this.addPersonSelected = null

      Object.assign(this.addPerson, BLANK_PERSON, {
        role,
        mode: 'new',
        date_of_birth: '',
        relationship_to_lead: 'partner',
        employment_status: '',
        annual_income: '',
        job_title: '',
        send_invitation: this.canInviteApplicant,
        // A sole tenant's guarantor stands behind the whole rent. Leaving this
        // blank or splitting it by accident is the commonest way a guarantee
        // ends up securing less than anyone in the room intended.
        guaranteed: applicants.length === 1 ? this.guaranteedKey(applicants[0]) : '',
        relationship: 'child',
        rent_liability_percentage: applicants.length === 1
          ? 100
          : Math.floor(100 / Math.max(applicants.length, 1)),
        declared_annual_income: '',
        occupation: ''
      })

      this.addPersonOpen = true
    },

    closeAddPerson() {
      this.addPersonOpen = false
      this.addPersonError = null
    },

    setAddPersonRole(role) {
      this.addPerson.role = role
      this.addPersonError = null
    },

    // ========== Derived ==========

    get addingGuarantor() {
      return this.addPerson.role === 'guarantor'
    },

    // Lives there without being on the tenancy: not referenced, income not
    // counted, but still owed ID and Right to Rent checks (the Home Office
    // asks the question of every adult occupier, tenant or not).
    get addingOccupier() {
      return this.addPerson.role === 'permitted_occupier'
    },

    // Applicants with a Person record are addressed by id; those still only on
    // the application form are addressed by email, and the server makes their
    // record when the guarantee is saved.
    guaranteedKey(applicant) {
      return applicant.id ? String(applicant.id) : `email:${applicant.email}`
    },

    get guaranteedChoice() {
      const chosen = String(this.addPerson.guaranteed || '')
      if (!chosen) return null
      return (this.guaranteeData.applicants || [])
        .find(a => this.guaranteedKey(a) === chosen) || null
    },

    get guaranteedName() {
      return this.guaranteedChoice?.name || ''
    },

    // They applied, but the application hasn't been invited to proceed, so they
    // have no tenant record yet. The guarantee still records now; only the deed
    // waits.
    get guaranteedIsPendingInvite() {
      return !!this.guaranteedChoice && !this.guaranteedChoice.id
    },

    // Before the household has been invited to proceed there is no portal to
    // invite anybody to — everyone's invitation goes out together at that point.
    get canInviteApplicant() {
      return ['pending_information', 'pending_referencing', 'pending_agreement']
        .includes(this.application?.status)
    },

    get addPersonCta() {
      if (this.addPersonSaving) return 'Saving…'
      if (this.addingGuarantor) return 'Save guarantor'
      if (this.addingOccupier) return 'Save occupier'
      return (this.addPerson.send_invitation && this.canInviteApplicant) ? 'Save & invite' : 'Save applicant'
    },

    get eighteenYearsAgo() {
      const d = new Date()
      d.setFullYear(d.getFullYear() - 18)
      return d.toISOString().slice(0, 10)
    },

    get addPersonComplete() {
      const p = this.addPerson
      const named = p.mode === 'existing'
        ? !!this.addPersonSelected
        : (p.first_name.trim() && p.last_name.trim())
      if (!named) return false

      if (this.addingGuarantor) {
        return !!p.guaranteed && !!p.date_of_birth && !!p.occupation.trim()
      }

      // An applicant is matched and invited by email, so it is required even
      // when they were picked from the existing-people search — a contact
      // recorded from a phone call may well not have one yet.
      return !!p.email.trim()
    },

    // What saving this person is about to change about the money. Stated before
    // the click rather than discovered afterwards.
    get addPersonAffordabilityNote() {
      if (this.addingGuarantor) return 'A guarantee only counts once the deed comes back signed.'
      if (this.addingOccupier) return "An occupier doesn't count towards affordability."

      const income = Number(this.addPerson.annual_income || 0)
      const household = Number(this.application?.total_household_income || 0)
      if (!income) return 'Affordability is recalculated on save.'
      return `Household income ${this.formatCurrency(household)} → ${this.formatCurrency(household + income)} on save.`
    },

    // ========== Finding somebody already on the books ==========

    searchAddPersonPeople() {
      clearTimeout(this.addPersonSearchTimer)
      const query = this.addPersonQuery.trim()

      if (query.length < 2) {
        this.addPersonResults = []
        return
      }

      this.addPersonSearchTimer = setTimeout(async () => {
        this.addPersonSearching = true
        try {
          // types[]=person, or the search hands back landlords, staff and
          // contractors — whose ids are not Person ids and would attach the
          // wrong record entirely.
          const response = await fetch(
            `/api/people/search?q=${encodeURIComponent(query)}&types[]=person`,
            { headers: { 'Accept': 'application/json' } }
          )
          if (!response.ok) throw new Error('Could not search people')
          const data = await response.json()
          const results = Array.isArray(data) ? data : (data.results || [])
          this.addPersonResults = results.filter(r => r.type === 'person').slice(0, 8)
        } catch (e) {
          this.addPersonError = e.message
        } finally {
          this.addPersonSearching = false
        }
      }, 250)
    },

    selectAddPersonResult(person) {
      this.addPersonSelected = person
      this.addPersonResults = []
      this.addPersonQuery = person.full_name || person.name || ''

      // Search returns a whole name; the forms behind this want it in two
      // halves. Splitting on the last space is right for the overwhelming
      // majority and stays editable for the rest.
      const whole = (person.full_name || person.name || '').trim()
      const cut = whole.lastIndexOf(' ')
      Object.assign(this.addPerson, {
        first_name: cut > 0 ? whole.slice(0, cut) : whole,
        last_name: cut > 0 ? whole.slice(cut + 1) : '',
        email: person.email || '',
        phone: person.phone || ''
      })
    },

    clearAddPersonSelection() {
      this.addPersonSelected = null
      this.addPersonQuery = ''
      this.addPersonResults = []
      Object.assign(this.addPerson, BLANK_PERSON)
    },

    // ========== Saving ==========

    async saveAddPerson() {
      if (!this.addPersonComplete || this.addPersonSaving) return

      this.addPersonSaving = true
      this.addPersonError = null

      try {
        if (this.addingGuarantor) {
          await this.postGuarantor()
        } else {
          await this.postApplicant()
        }

        // The guarantee panel travels with the application, so one fetch
        // redraws both the applicant tabs and the guarantor tabs.
        this.addPersonOpen = false
        await this.fetchApplication()
        this.landOnNewPersonTab()
      } catch (e) {
        this.addPersonError = e.message
      } finally {
        this.addPersonSaving = false
      }
    },

    async postApplicant() {
      const p = this.addPerson
      const occupier = this.addingOccupier
      const payload = {
        applicant: {
          first_name: p.first_name,
          last_name: p.last_name,
          email: p.email,
          phone: p.phone,
          date_of_birth: p.date_of_birth || null,
          relationship_to_lead: p.relationship_to_lead,
          // An occupier was never asked these — send nothing rather than
          // whatever a half-toggled form left behind.
          employment_status: occupier ? null : (p.employment_status || null),
          job_title: occupier ? null : (p.job_title || null),
          annual_income: occupier || p.annual_income === '' ? null : Number(p.annual_income),
          role: occupier ? 'permitted_occupier' : null,
          address_line_1: p.address_line_1,
          address_line_2: p.address_line_2,
          town: p.town,
          county: p.county,
          postcode: p.postcode
        },
        // Occupiers have no portal — there is nothing to invite them to.
        send_invitation: !occupier && p.send_invitation && this.canInviteApplicant
      }

      const data = await this.postAddPerson(
        `/tenancy-applications/${this.applicationId}/applicants`,
        payload,
        'Could not add this applicant'
      )
      return data
    },

    async postGuarantor() {
      const p = this.addPerson
      const income = p.declared_annual_income
      const payload = {
        guarantee: {
          relationship: p.relationship,
          date_of_birth: p.date_of_birth,
          occupation: p.occupation,
          rent_liability_percentage: p.rent_liability_percentage,
          declared_annual_income_pence: income === '' ? null : Math.round(Number(income) * 100)
        }
      }

      // An applicant who has no Person record yet is named by email — saving
      // the guarantee is what creates their record.
      const chosen = String(p.guaranteed || '')
      if (chosen.startsWith('email:')) {
        payload.guaranteed_email = chosen.slice('email:'.length)
      } else {
        payload.guarantee.guaranteed_person_id = chosen
      }

      if (p.mode === 'existing' && this.addPersonSelected) {
        payload.guarantor_person_id = this.addPersonSelected.id
      } else {
        payload.guarantor = {
          first_name: p.first_name,
          last_name: p.last_name,
          email: p.email,
          phone: p.phone,
          address_line_1: p.address_line_1,
          address_line_2: p.address_line_2,
          town: p.town,
          county: p.county,
          postcode: p.postcode
        }
      }

      return this.postAddPerson(
        `/tenancy-applications/${this.applicationId}/guarantees`,
        payload,
        'Could not save this guarantor'
      )
    },

    async postAddPerson(url, payload, fallbackError) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error((data.errors || [fallbackError]).join(', '))
      return data
    },

    // Show the work: open the tab of the person just added rather than leaving
    // the reader to find them.
    landOnNewPersonTab() {
      if (this.addingGuarantor) {
        const count = this.guarantees.length
        if (count > 0) {
          this.switchToPersonTab(`guarantor_${count}`, this.guarantees[count - 1].guarantor_person_id)
        }
      } else {
        const others = this.application?.other_applicants || []
        if (others.length > 0) {
          const index = others.length - 1
          this.switchToPersonTab(`applicant_${index + 2}`, others[index].person_id)
        }
      }
    }
  }
}
